import React, { useEffect, useState, createContext, useContext, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { ELSI_STATIC_URL, errorCallback, makeUrl, EMPTY_IMAGE_SRC } from "../../const/const";
import NavigationBlock from "./Navigation-block";
import { getFetchRequest, postFetchRequestWithSignal, MAKE_PRODUCT_REQUEST, GET_STOCKS, MAKE_MANUFACTURER_REQUEST, CONFIGS_URL, postFetchRequest } from "../../store/requests";
import { ErrorHandler } from "./ErrorHandler";
import { findNotEmptyImageName, NavigationBlockContext, SEND_PRICES_REQUEST } from "../../const/const";
import GetPricePopUp from "./GetPricePopUp";
import { ChevronIcon } from "../../appearance/icons/chevron_icon";
import Loader from "./Loader";
import LoadingIndicator from "./LoadingIndicator";
import { clearPricesAsyncService, handleCreateNewPriceAsyncService, registerObserver, unRegisterObserver } from "./priceAsyncService";
import CartHandler from "./CartHandler";
import CountButton from "./CountButton";
import { cartManager, sortByQuantity } from "./cartManager";
import { popupOpenService } from "./popupOpenService";

export const ProductContext = createContext();
const PRODUCT_ATTRIBUTE_CATEGORY = 'Category';
const CONFIG_CAN_GUEST_SEE_PRICES = 'guest-can-see-prices';
const PRODUCT_PLACEHOLDER_IMAGE_URL = 'http://cdn.auroraelec.ru/static/250px.svg';

const productClassificationList = [
    'rohsStatus',
    'moistureSensitiveLevel',
    'reachStatus',
    'eccn',
    'htsus',
    'californiaProp65',
];

const getValueByKeyClassification = (key, listOfClassification) => {
    let result = '-';
    if(listOfClassification&&listOfClassification[0]&&listOfClassification.length) {
        Object.entries(listOfClassification[0]).forEach(it => it[0]===key? result=it[1]:'');
    }
    return result;
};

const canUserGetPrice = (configs) => {
    // Разрешаем цены, если:
    // 1) включен флаг для гостей
    // 2) пользователь авторизован (есть токен), даже если гостям запрещено
    const guestCanSee = configs.findIndex(
        (it) => it.configName === CONFIG_CAN_GUEST_SEE_PRICES && it.isActive,
    ) >= 0;
    const hasToken =
        typeof window !== 'undefined' && !!localStorage.getItem('token');
    return guestCanSee || hasToken;
};

const findDatasheetData = (product, key) => {
    let result = '';
    product[key] ? result = product[key] : null;
    return result;
};

const getDatasheetName = (url) => {
    if(url) {
        const result = url.split('/').reverse()[0];
        return result;
    }
    return '';
};

const getImageExternalUrl = (image) => image?.externalUrl || image?.external_url || '';

const getImageHostname = (image) => {
    const raw = getImageExternalUrl(image);
    if (!raw) {
        return '';
    }
    try {
        return new URL(raw).hostname.toLowerCase();
    } catch (e) {
        return String(raw).toLowerCase();
    }
};

const isDigikeyImage = (image) => {
    const host = getImageHostname(image);
    return host === 'digikey.com' || host.endsWith('.digikey.com');
};

const isMouserImage = (image) => {
    const host = getImageHostname(image);
    return host === 'mouser.com' || host.endsWith('.mouser.com');
};

// Только файлы с нашего S3. Главная — DigiKey, затем Mouser, затем остальные
const getOwnCdnImages = (images) => {
    const withName = (images || []).filter((it) => it?.name);
    const fromDigikey = withName.filter(isDigikeyImage);
    const fromMouser = withName.filter((it) => isMouserImage(it) && !isDigikeyImage(it));
    const rest = withName.filter((it) => !isDigikeyImage(it) && !isMouserImage(it));
    return [...fromDigikey, ...fromMouser, ...rest];
};

// Функции для определения состояния загрузки
const isImageLoading = (product) => {
    // Если есть документация (datasheetName), но нет изображения - значит изображения не существует
    if (product.datasheetName && (!product.images || !product.images.length || !findNotEmptyImageName(product.images))) {
        return false; // Показываем заглушку, а не загрузку
    }
    
    // Изображение загружается если есть externalUrl, но нет локального файла
    if (product.images && product.images.length > 0) {
        const firstImage = product.images[0];
        return firstImage.externalUrl && !findNotEmptyImageName(product.images);
    }
    return false;
};

const isDatasheetLoading = (product) => {
    // Документация загружается если есть externalDatasheet, но нет datasheetName
    return product.externalDatasheet && !product.datasheetName;
};

const PdfViewer = ({ product }) => {
    if (!product.pdfFileName) return null;

    return (
        <div className="product-item__pdf-section">
            <h3 className="product-item__info-title">
                Документация (PDF):
            </h3>
            <a
                href={`${ELSI_STATIC_URL}/${product.id}/${product.pdfFileName}`}
                target="_blank"
                rel="noopener noreferrer"
                className="product-item__pdf-link"
            >
                {product.pdfFileName}{' '}
                ({Math.round(product.pdfFileSize / 1024)} KB)
            </a>
            <iframe
                src={`${ELSI_STATIC_URL}/${product.id}/${product.pdfFileName}#view=fitH`}
                className="product-item__pdf-viewer"
                title="PDF Viewer"
            />
        </div>
    );
};

const findCurrentPrice = (price) => {
    const currentPricesList = Object.values(cartManager.getCurrentPrices());
    if(currentPricesList && currentPricesList.length) {
        let result = [];
        currentPricesList.forEach(it => result.push(it.quantities.sort(sortByQuantity).findLast(item => item.minQty <= it.count)));
        return result.find(it => it && it.id === price.id)  ? result.find(it => it && it.id === price.id).id : '';
    } else {
        return '';
    }
};

const checkPricesReady = (pricesArray, priceDataIsAbsence) => {
    return ((priceDataIsAbsence && pricesArray.length) || !priceDataIsAbsence);
};

const generateMainTitle = (partNumber, description) => {
    let result = partNumber;
    if(result && result.length<40 && description && description.length) {
        result+=', ';
        const descriptionData = description.split(' ');
        if(descriptionData.length > 2 && (descriptionData[0]+descriptionData[1]+descriptionData[2]).length<40) {
            result+=descriptionData[0]+' '+descriptionData[1]+' '+descriptionData[2];
        } else if(descriptionData.length> 1 &&(descriptionData[0]+descriptionData[1]).length<40) {
            result+=descriptionData[0]+' '+descriptionData[1];
        } else if(descriptionData[0].length<40) {
            result+=descriptionData[0];
        }
    }
    return result;
};

const MainTitle = ({product}) => {
    try {
        return (
            <h1 className="product-item__title">{generateMainTitle(product.partNumber, product.description)}</h1>
        );
    } catch(error) {
        console.log(error);
        return null;
    }
};

const PhotoItem = ({ product }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const imageIsLoading = isImageLoading(product);
    const ownCdnImages = useMemo(() => getOwnCdnImages(product.images), [product.images]);

    useEffect(() => {
        setCurrentImageIndex(0);
    }, [product.id]);

    const image = ownCdnImages[currentImageIndex];
    const currentSrc = image?.name
        ? `${ELSI_STATIC_URL}/${product.id}/${image.name}`
        : PRODUCT_PLACEHOLDER_IMAGE_URL;

    const handleError = () => {
        if (currentImageIndex < ownCdnImages.length - 1) {
            setCurrentImageIndex((prev) => prev + 1);
            return;
        }
    };

    if (imageIsLoading) {
        return (
            <div className="product-item__photo-item">
                <LoadingIndicator 
                    type="image" 
                    text="Загрузка изображения..."
                    className="product-item__photo-loading"
                />
            </div>
        );
    }

    return (
        <img 
            className="product-item__photo-item" 
            alt={`${product.partNumber} photo`}
            src={currentSrc}
            onError={handleError}
        />
    );
};

const ManufacturerInfo = ({product, isDescription, handleDownloadDatasheet}) => {
    try {
        return (
            product.manufacturer && product.manufacturer.id ?
                !isDescription ? 
                    handleDownloadDatasheet ? 
                        // Показываем секцию с документацией или загрузкой
                        <div className="product-item__doc-section" key={product.manufacturer.id + 'data'}>
                            {isDatasheetLoading(product) ? (
                                <LoadingIndicator 
                                    type="text" 
                                    text="Загрузка документации..."
                                    className="product-item__datasheet-loading"
                                />
                            ) : (
                                <p className="product-item__link" onClick={() => handleDownloadDatasheet(product)}>
                                    {product.datasheetName?.split('/').pop() || getDatasheetName(product.externalDatasheet)}
                                </p>
                            )}
                        </div>
                        :
                        <p className="product-item__manufacturer" key={product.manufacturer.id + 'mfr'}>
                            <span className="product-item__manufacturer-name">{product.manufacturer.name}</span>
                            {product.manufacturer.logo ? 
                                <img className="product-item__manufacturer-image"
                                    onError={(e) => {
                                        e.target.src=EMPTY_IMAGE_SRC;
                                        e.target.onerror = null;
                                    }}
                                    src={`${ELSI_STATIC_URL}/manufacturers/${product.manufacturer.logo}`} /> 
                                :
                                <img className="product-item__manufacturer-image" src={EMPTY_IMAGE_SRC} />}
                        </p>
                    : 
                    <p className="product-item__description" key={product.manufacturer.id + 'desc'}>
                        {product.description}
                    </p>
                :
                ''
        );
    } catch (error) {
        console.log(error);
        return null;
    }
};

const AttributesList = ({product, classificationIsOpen, rowRefs, startIndex}) => {
    try {
        let visibleIndex = 0;
        return (
            product.attributes && product.attributes.length ? product.attributes.map((it, index) => {
                if (it.attributeKey === PRODUCT_ATTRIBUTE_CATEGORY) return null;
                if (!it.attributeValue || it.attributeValue.trim() === '') return null;
                const refIndex = startIndex + visibleIndex;
                visibleIndex++;
                return (
                    <tr
                        key={it.attributeKey+index}
                        className="product-table__row"
                        ref={el => rowRefs.current[refIndex] = el}
                    >
                        <td className="product-table__item"><p>{it.attributeKey}</p></td>
                        <td className="product-table__item"><p>{it.attributeValue}</p></td>
                    </tr>
                );
            }) : null
        );
    } catch (error) {
        console.log(error);
        return null;
    }
};

const ClassificationList = ({product, classificationIsOpen, rowRefs, startIndex}) => {
    try {
        let visibleIndex = 0;
        return (
            productClassificationList.map((it, index) => {
                const value = getValueByKeyClassification(it, product.classifications);
                if (value === '-' || !value || value.trim() === '') return null;
                const refIndex = startIndex + visibleIndex;
                visibleIndex++;
                return (
                    <tr
                        key={it+'attr'}
                        className="product-table__row"
                        ref={el => rowRefs.current[refIndex] = el}
                    >
                        <td className="product-table__item"><p>{it}</p></td>
                        <td className="product-table__item"><p>{value}</p></td>
                    </tr>
                );
            })
        );
    } catch (error) {
        console.log(error);
        return null;
    }
};

export const PricesList = ({pricesArray, activePricesIsChanged, activePrice=null, duty}) => {
    try {
        console.log('[PricesList] Initial props:', {
            pricesArray,
            duty,
            activePricesIsChanged,
            activePrice,
        });

        const parseDuty = (dutyString) => {
            console.log('[parseDuty] Input duty string:', dutyString);
            
            if (!dutyString) {
                console.warn('[parseDuty] Duty string is empty');
                return 0;
            }
            
            // Пробуем разные форматы
            const match = dutyString.match(/(\d+\.?\d*)/); // Ищем любое число (включая с точкой)
            const result = match ? parseFloat(match[1]) / 100 : 0;
            
            console.log('[parseDuty] Parsed result:', result);
            return result;
        };

        const calculatePriceWithDuty = (priceValue, duty) => {
            const dutyFactor = parseDuty(duty);
            const finalPrice = priceValue * (1 + dutyFactor);
            
            console.log('🎯 [ProductPage-PricesList] Second duty:', {
                priceValue,
                dutyFactor,
                finalPrice,
            });
            
            return finalPrice;
        };

        const dutyPercentage = parseDuty(duty);
        console.log('[Render] Duty percentage:', dutyPercentage);
        console.log('[Render] Prices array:', pricesArray);

        return (
            pricesArray.length ?
                <ul className="product-table__list">
                    {pricesArray.sort((a,b) => a.minQty - b.minQty).map(item => {
                        console.log('[Item Render] Processing item:', item);
                        
                        const finalPrice = calculatePriceWithDuty(item.priceValue, duty);
                        const displayPrice = finalPrice.toFixed(2);
                        
                        console.log('[Item Render] Final display price:', displayPrice);
                        
                        return (
                            <li key={item.id + activePricesIsChanged} 
                                className={`product-price__table-list 
                                    ${findCurrentPrice(item) === item.id 
                                        || (activePrice && activePrice.priceId === item.priceId) ? 'product-price__table-list_active':''}`}>
                                <span>{item.minQty}+</span>
                                <span>
                                    {displayPrice} {(item.currency === 'USD' || (typeof localStorage !== 'undefined' && localStorage.getItem('elsi_price_currency') === 'USD')) ? '$' : '₽'}
                                </span>
                            </li>
                        );
                    })}
                </ul> : null
        );
    } catch(error) {
        console.error('[PricesList Error]', error);
        return null;
    }
};

const createKeyForPricesTable = (price) => {
    return price.manufacturer+price.distributor+price.packer+price.id;
};

const checkPossibilityToCreateWSConnection = (productId, partNumber, configsList) => {
    return productId && partNumber && canUserGetPrice(configsList);
};

const CompareAndFavoriteButtons = () => {
    const [isCompareChecked, setIsCompareChecked] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);

    const handleCompareClick = () => {
        setIsCompareChecked(!isCompareChecked);
    };

    const handleFavoriteClick = () => {
        setIsFavorite(!isFavorite);
    };

    return (
        <div className="product-buttons-container" style={{ opacity: '0' }}>
            <button 
                className={`product-compare-button ${isCompareChecked ? 'active' : ''}`}
                onClick={handleCompareClick}
            >
                <input 
                    type="checkbox" 
                    checked={isCompareChecked}
                    readOnly
                />
                <span>Сравнить</span>
            </button>
            
            <button 
                className={`product-favorite-button ${isFavorite ? 'active' : ''}`} 
                onClick={handleFavoriteClick}
            >
                <svg width="16" height="16" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.44 11.25L19 6.75L12.54 6L9.5 0L6.46 6L0 6.75L4.56 11.625L3.8 18L9.5 15L15.2 18L14.44 11.25Z" 
                        stroke={isFavorite ? "#0f8cc8" : "#86868E"} fill={isFavorite ? "#0f8cc8" : "none"}/>
                </svg>
                <span>Добавить в избранное</span>
            </button>
        </div>
    );
};

export default function ProductItem() {
    const [product, setProduct] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState({type:'', message:''});
    const [pricesArray, setPricesArray] = useState([]);
    const [classificationIsOpen, setClassificationIsOpen] = useState(false);
    const [configsList, setConfigsList] = useState([]);
    const [activePricesIsChanged, setActivePricesIsChanged] = useState(false);
    const [priceDataIsAbsence, setPriceDataIsAbsence] = useState(false);
    const [ currentPath, setCurrentPath, categories, setCategories,title, setTitle, router, CustomRouter ] = useContext(NavigationBlockContext);
    const id = CustomRouter.getParams('id');
    let controller = new AbortController();
    const [blockedStocks, setBlockedStocks] = useState([]);
    const [stockCoefs, setStockCoefs] = useState({});
    const [isMobile, setIsMobile] = useState(false);

    // refs для раскрывающегося блока и строк
    const collapsibleRef = useRef(null);
    const rowRefs = useRef([]);
    const [collapsibleMaxHeight, setCollapsibleMaxHeight] = useState('none');
    const refreshIntervalRef = useRef(null);

    const parseDuty = (dutyString) => {
        const match = dutyString?.match(/(\d+\.?\d*)/);
        return match ? parseFloat(match[1])/100 : 0;
    };

    const successGetBlockedStocks = (response) => {
        const blockedNames = response.names || [];
        console.log('🎯 [ProductPage] Loaded blocked stocks:', blockedNames);
        setBlockedStocks(blockedNames);
    };

    useEffect(() => {
        postFetchRequest(
            makeUrl(['/api/v1/product/get-stocks/']),
            {}, 
            successGetBlockedStocks,    
            errorCallback,
        );
    }, []);

    const applyDutyToPrice = (price, duty) => {
        const dutyFactor = parseDuty(duty);
        if(dutyFactor > 1) return price;
      
        return {
            ...price,
            quantities: price.quantities.map(q => ({
                ...q,
                priceValue: q.priceValue * (1 + dutyFactor),
            })),
        };
    };
    
    const changePricesArray = (priceData) => {
        if(Array.isArray(priceData)) {
            console.log("🎯 [ProductPage] Updated pricesArray:", priceData);
            
            // Показываем первые несколько элементов для отладки
            priceData.slice(0, 2).forEach(supplier => {
                console.log(`🎯 [ProductPage] Supplier: ${supplier.distributor || supplier.source}, quantities:`, supplier.quantities?.slice(0, 3));
            });
            
            setPricesArray([...priceData]);
        } else {
            setPriceDataIsAbsence(true);
        }
    };

    useEffect(() => {
        getFetchRequest(
            makeUrl(['/api/v1/product/get-stocks-coef/']),
            response => {
                console.log('🎯 [ProductPage] Loaded stock coefficients:', response);
                setStockCoefs(response);
            },
            errorCallback,
        );
    }, []);



    useEffect(() => {
        if (checkPossibilityToCreateWSConnection(id, product.partNumber, configsList)) {
            handleCreateNewPriceAsyncService(id, product.partNumber);
            registerObserver(changePricesArray);
        }
        return () => {
            unRegisterObserver(changePricesArray);
            setPricesArray([]);
            cartManager.clearService();
        };
    }, [id, product.partNumber, configsList]);

    const successGetDataCallback = (response, result, data) => {
        if(data.type==="IMAGE") {
            result.images[0].name = response.resourceName;
        }
        setProduct({...result});
        setLoading(false);
    };

    // Функция для обновления данных товара
    const refreshProductData = useCallback(() => {
        if (id) {
            getFetchRequest(makeUrl([MAKE_PRODUCT_REQUEST, '/', id]), (result) => {
                // Обновляем только данные товара, не запускаем новые запросы краулера
                setProduct(result);
            }, errorCallback);
        }
    }, [id]);

    // Функция для управления автоматическим обновлением
    const manageAutoRefresh = useCallback((productData) => {
        const needsRefresh = isImageLoading(productData) || 
                            isDatasheetLoading(productData) || 
                            productData.enrichmentInProgress; // Проверяем флаг обогащения
        
        if (needsRefresh && !refreshIntervalRef.current) {
            // Запускаем автоматическое обновление каждые 5 секунд
            console.log('🔄 [ProductItem] Starting auto-refresh:', {
                imageLoading: isImageLoading(productData),
                datasheetLoading: isDatasheetLoading(productData),
                enrichmentInProgress: productData.enrichmentInProgress,
            });
            refreshIntervalRef.current = setInterval(() => {
                refreshProductData();
            }, 5000);
        } else if (!needsRefresh && refreshIntervalRef.current) {
            // Останавливаем автоматическое обновление
            console.log('⏹️ [ProductItem] Stopping auto-refresh');
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }
    }, [refreshProductData]);

    const successCallback = async (result) => {
        const responseData = await result;       
        if(findDatasheetData(responseData, 'externalDatasheet')) {
            const data = {
                type: "DATASHEET",
                url: findDatasheetData(responseData, 'externalDatasheet'),
            };
            postFetchRequestWithSignal(makeUrl([MAKE_PRODUCT_REQUEST, '/', responseData.id, '/resource/']), data, (response)=>successGetDataCallback(response, result, data), errorCallback, controller);
        }
        if(responseData.images&&responseData.images.length&&!findNotEmptyImageName(responseData.images)) {
            if(responseData.images[0].externalUrl) {
                const data = {
                    type: "IMAGE",
                    url: responseData.images[0].externalUrl,
                };
                postFetchRequestWithSignal(makeUrl([MAKE_PRODUCT_REQUEST,'/', responseData.id, '/resource/']), data, (response)=>successGetDataCallback(response, result, data), errorCallback, controller);
            }
        }
        if(responseData.manufacturer && responseData.manufacturer.logo === null) {
            postFetchRequestWithSignal(makeUrl([MAKE_MANUFACTURER_REQUEST, responseData.manufacturer.id, '/resource/']), null, () => null, errorCallback, controller);
        }
        setTitle(responseData.partNumber);
        setProduct(responseData);
        getFetchRequest(makeUrl([MAKE_PRODUCT_REQUEST, '/', responseData.id, '/category/']), 
            (response) => response.length&&response[0]&&setCategories(response[0]), errorCallback);
        setLoading(false);
        
        // Запускаем автоматическое обновление если нужно
        manageAutoRefresh(responseData);
    };

    const changeActivePrices = () => {
        setActivePricesIsChanged(prev => !prev);
    };

    useEffect(() => {
        if (id) {
            // Обеспечиваем показ лоадера при переходе между товарами
            setLoading(true);
            setCurrentPath(`/product/${id}`);
            getFetchRequest(makeUrl([MAKE_PRODUCT_REQUEST, '/', id, '/']), successCallback, errorCallback);
            getFetchRequest(makeUrl([CONFIGS_URL]), (response) => setConfigsList(response), errorCallback);
            cartManager.registerCartObserver(changeActivePrices);
        }
        return () => {
            // При смене id сразу включаем лоадер, чтобы не показывать пустую карточку
            setLoading(true);
            controller.abort();
            cartManager.unRegisterCartObserver(changeActivePrices);
            setPriceDataIsAbsence(false);
            clearPricesAsyncService();
            setProduct({});
        };
    }, [id]);

    // Отслеживаем изменения товара для управления автообновлением
    useEffect(() => {
        if (product && Object.keys(product).length > 0) {
            manageAutoRefresh(product);
        }
        
        // Очищаем интервал при размонтировании компонента
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [product, manageAutoRefresh]);

    const handleNavigate = () => {
        CustomRouter.navigate(`/login`);
    };

    const handleDownloadDatasheet = () => {
        const tryDownload = (url) => {
            return new Promise((resolve) => {
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                
                // Проверяем куда ведет ссылка после клика
                link.onclick = (e) => {
                    const finalUrl = e.target.href;
                    if (finalUrl.includes('auroraelectronics.ru/product/') || finalUrl.includes('auroraelec.ru/product/')) {
                        e.preventDefault();
                        resolve('invalid-url');
                        return false;
                    }
                    return true;
                };
                
                link.onload = () => {
                    document.body.removeChild(link);
                    resolve('success');
                };
                
                link.onerror = () => {
                    document.body.removeChild(link);
                    resolve('error');
                };

                document.body.appendChild(link);
                link.click();
            });
        };

        // Получаем оба варианта URL
        const newUrl = findDatasheetData(product, 'datasheetName');
        const oldUrl = `${ELSI_STATIC_URL}/${product.id}/${findDatasheetData(product, 'datasheetName')}`;

        // Последовательные попытки
        const executeAttempts = async () => {
            // Сначала пробуем новый URL
            const firstAttempt = await tryDownload(newUrl);
            
            // Если URL запрещен или ошибка - пробуем старый вариант
            if (firstAttempt === 'invalid-url' || firstAttempt === 'error') {
                await tryDownload(oldUrl);
            }
        };

        executeAttempts().catch(() => {
            setError({ type: 'datasheet', message: 'Ошибка загрузки файла' });
        });
    };

    const handleOpenPopUp = () => {
        popupOpenService.callToOpen(SEND_PRICES_REQUEST);
    };

    // вычисляем видимые строки для анимации раскрытия
    const visibleAttributes = product.attributes?.filter(it => it.attributeKey !== PRODUCT_ATTRIBUTE_CATEGORY) || [];
    const visibleClassifications = productClassificationList
        .map(it => getValueByKeyClassification(it, product.classifications))
        .filter(val => val !== '-');
    const totalRows = visibleAttributes.length + visibleClassifications.length;

    useLayoutEffect(() => {
        if (!collapsibleRef.current) return;
        // Получаем все строки
        const rows = rowRefs.current.filter(Boolean);
        if (rows.length === 0) {
            setCollapsibleMaxHeight('none');
            return;
        }
        if (classificationIsOpen) {
            // Высота всех строк
            const totalHeight = rows.reduce((sum, row) => sum + row.offsetHeight, 0);
            setCollapsibleMaxHeight(`${totalHeight}px`);
        } else {
            // Высота первых 9 строк
            const visibleRows = rows.slice(0, 9);
            const collapsedHeight = visibleRows.reduce((sum, row) => sum + row.offsetHeight, 0);
            setCollapsibleMaxHeight(`${collapsedHeight}px`);
        }
    }, [classificationIsOpen, product]);

    // Функция для проверки размера экрана и обновления состояния
    const handleResize = useCallback(() => {
        setIsMobile(window.innerWidth < 1000);
    }, []);
    
    // Добавляем слушатель изменения размера окна
    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);
    
    // Функция для группировки элементов таблицы на мобильных устройствах
    const renderResponsiveTableRow = (price) => {
        const warehouseKey = price.distributor || price.source;
        const coef = stockCoefs[warehouseKey] ?? 1;

        console.log(`🎯 [ProductPage] Supplier: ${warehouseKey}, coef: ${coef}, quantities: ${price.quantities.length}`);

        const pricedWithCoef = {
            ...price,
            quantities: price.quantities.map(q => {
                const adjustedPrice = q.priceValue * coef;
                console.log(`🎯 [ProductPage] ${q.minQty}+ ${q.priceValue} * ${coef} = ${adjustedPrice}`);
                return {
                    ...q,
                    priceValue: adjustedPrice,
                };
            }),
        };

        // Улучшенная логика обработки пошлины
        let dutySource = null;
        if (product.duty) {
            dutySource = product.duty;
        } else if (product.datasheetSize && parseFloat(product.datasheetSize) <= 100) {
            dutySource = product.datasheetSize;
        }
        
        const dutyFactor = dutySource ? parseDuty(dutySource) : 0;
        console.log(`🎯 [ProductPage] DutySource: ${dutySource}, dutyFactor: ${dutyFactor}`);
        
        const priceWithDuty = dutyFactor <= 1
            ? {
                ...pricedWithCoef,
                quantities: pricedWithCoef.quantities.map(q => {
                    const priceWithFirstDuty = q.priceValue * (1 + dutyFactor);
                    console.log(`🎯 [ProductPage] First duty: ${q.priceValue} * (1 + ${dutyFactor}) = ${priceWithFirstDuty}`);
                    return {
                        ...q,
                        priceValue: priceWithFirstDuty,
                    };
                }),
            }
            : pricedWithCoef;

        // Определяем минимальный заказ как минимальный порог minQty (не разницу ценовых уровней)
        const sortedQuantities = Array.isArray(price?.quantities)
            ? [...price.quantities].sort(sortByQuantity)
            : [];
        const minOrderQuantity = sortedQuantities.length > 0 ? sortedQuantities[0].minQty : null;
        
        // Кратность равна минимальному заказу (если продается от 1 шт — кратность 1)
        const multiplicity = minOrderQuantity ?? 1;

        if (isMobile) {
            return (
                <tr
                    className="product-price__table-row"
                    key={createKeyForPricesTable(price)}
                >
                    <div className="product-price__table-group first">
                        <td className="product-price__table-item mob-title">
                            <span>{price.partNumber}</span>
                            <span>{price.manufacturer}</span>
                        </td>
                        <td className="product-price__table-item">
                            {price.stockNumber} шт.
                        </td>
                    </div>
                    <div className="product-price__table-group sec">
                        <td className="product-price__table-item">
                            Отгрузка:<br />
                            {price.distributor || price.source}<br />
                            {price.packer && <>Уп. {price.packer}</>}
                            {minOrderQuantity && (
                                <div className="order-rules">
                                    Мин. объем<br />
                                    заказа {minOrderQuantity} шт.<br />
                                    Кратность {multiplicity} шт.
                                </div>
                            )}
                        </td>
                    </div>
                    <div className="product-price__table-group third">
                        <td className="product-price__table-item">
                            <PricesList
                                pricesArray={priceWithDuty.quantities}
                                activePricesIsChanged={activePricesIsChanged}
                                duty={dutySource}
                            />
                        </td>
                        <td className="product-price__table-item">
                            <CountButton
                                price={priceWithDuty}
                                originalPrice={price}
                                duty={dutySource}
                            />
                        </td>
                    </div>
                </tr>
            );
        }

        return (
            <tr
                className="product-price__table-row"
                key={createKeyForPricesTable(price)}
            >
                <td className="product-price__table-item first">
                    {price.partNumber}
                </td>
                <td className="product-price__table-item">
                    {price.manufacturer}
                </td>
                <td className="product-price__table-item">
                    {price.stockNumber} шт.
                </td>
                <td className="product-price__table-item" />
                <td className="product-price__table-item">
                    {minOrderQuantity && (
                        <div className="order-rules">
                            Мин. объем<br />
                            заказа {minOrderQuantity} шт.<br />
                            Кратность {multiplicity} шт.
                        </div>
                    )}
                </td>
                <td className="product-price__table-item">
                    {price.distributor || price.source}
                    {price.packer && (
                        <p className="product-price__table-packer">
                            Уп. {price.packer}
                        </p>
                    )}
                </td>
                <td className="product-price__table-item">
                    <PricesList
                        pricesArray={priceWithDuty.quantities}
                        activePricesIsChanged={activePricesIsChanged}
                        duty={dutySource}
                    />
                </td>
                <td className="product-price__table-item">
                    <CountButton
                        price={priceWithDuty}
                        originalPrice={price}
                        duty={dutySource}
                    />
                </td>
            </tr>
        );
    };

    try {
        return (
            (loading || !product || !product.id) ? <Loader product={true} /> :
                <>
                    {Object.keys(product).length ? <NavigationBlock /> : ''}
                    <ProductContext.Provider value={[
                        canUserGetPrice(configsList, pricesArray) && checkPricesReady(pricesArray, priceDataIsAbsence), product]
                    }>
                        <GetPricePopUp/>
                    </ProductContext.Provider>
                    <MainTitle product={product} />
                    <div className="product-item__wrapper">
                        <section className="product-item">
                            <div className="product-item__photo">
                                <PhotoItem product={product}/>
                                <p className="product-item__photo-link">
                                    Изображения только для справки
                                    См. технические характеристики продукта
                                </p>
                            </div>
                        </section>
                        <section className="product-item__section">
                            <div className="product-item__info">
                                <h3 className="product-item__info-title">Номер по каталогу производителя:</h3>
                                <p className="product-item__partnumber">{product.partNumber}</p>
                                <div className="product-item__wrapper-manufacturer">
                                    <h3 className="product-item__info-title">Бренд / Производитель:</h3>
                                    <ManufacturerInfo product={product} isDescription={false}/>
                                </div>
                                <h3 className="product-item__info-title">Описание:</h3>
                                <ManufacturerInfo product={product} isDescription={true}/>
                                <h3 className="product-item__info-title">Техническая документация:</h3>
                                <ManufacturerInfo product={product} isDescription={false} handleDownloadDatasheet={handleDownloadDatasheet}/>
                            </div>
                        </section>
                        <section className="product-table__block">
                            <h2 className="product-table__title">Технические характеристики: </h2>
                            <table className="product-table product-table__attributes">
                                <tbody>
                                    <tr>
                                        <td colSpan={2} style={{padding: 0, border: 'none'}}>
                                            <div
                                                ref={collapsibleRef}
                                                className={`product-table__collapsible${classificationIsOpen ? ' open' : ''}`}
                                                style={{ maxHeight: collapsibleMaxHeight, transition: 'max-height 0.4s ease' }}
                                            >
                                                <table style={{width: '100%'}}>
                                                    <tbody>
                                                        <AttributesList product={product} classificationIsOpen={classificationIsOpen} rowRefs={rowRefs} startIndex={0}/>
                                                        <ClassificationList product={product} classificationIsOpen={classificationIsOpen} rowRefs={rowRefs} startIndex={visibleAttributes.length}/>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            {product.attributes&&product.attributes.length + productClassificationList.length > 9 ? 
                                <p className="product-table__subtitle product-table__subtitle_bottom" onClick={() => setClassificationIsOpen(!classificationIsOpen)}>
                                    {!classificationIsOpen?'Развернуть':'Свернуть'}
                                    <ChevronIcon nameOfClass={`product-table__icon ${classificationIsOpen?'product-table__icon_collapsed':''}`} />
                                </p>:''}
                        </section> 
                    </div>
                    
                    {/* Добавляем блок с кнопками "Сравнить" и "Добавить в избранное" */}
                    <CompareAndFavoriteButtons />
                    
                    <section className="product-price__block">
                        {canUserGetPrice(configsList) ?
                            <table className="product-table product-price__table">
                                <thead>
                                    <tr className="product-price__table-header">
                                        <th className="product-price__table-item">Наименование</th>
                                        <th className="product-price__table-item">Производитель</th>
                                        <th className="product-price__table-item">Наличие</th>
                                        <th className="product-price__table-item">Партия</th>
                                        <th className="product-price__table-item">Правила заказа</th>
                                        <th className="product-price__table-item">Отгрузка</th>
                                        <th className="product-price__table-item">Цена</th>
                                        <th className="product-price__table-item">Добавить в заказ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {((pricesArray && pricesArray.length) || priceDataIsAbsence) ? (
                                        pricesArray && pricesArray.length ? (
                                            pricesArray
                                                // убираем прайсы с заблокированных складов
                                                .filter(price =>
                                                    !blockedStocks.includes(price.distributor || price.source),
                                                )
                                                .map(price => renderResponsiveTableRow(price))
                                        ) : (
                                            <tr>
                                                <td colSpan="8">
                                                    <div className="product-price__empty product-price__empty_center">
                                                        Цены временно недоступны: склады не вернули предложений по данному товару. Вы можете
                                                        <span className="product-price__empty-link" onClick={handleOpenPopUp}> отправить запрос </span>
                                                        на получение цены.
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    ) : (
                                        <tr>
                                            <td colSpan="8">
                                                <Loader isProductPrice={true} />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>:<p className="product-price__empty">
                                {CustomRouter.needToUseCabinetStyles()||(typeof localStorage !== 'undefined' && localStorage.getItem('token')) ? "" : 
                                    <>
                                        <span className="product-price__empty-link" onClick={handleNavigate}>
                                            Авторизуйтесь
                                        </span> или 
                                    </>}
                                <span className="product-price__empty-link" onClick={handleOpenPopUp}>
                                    {" "+"отправьте запрос"+" "}
                                </span>
                                на получение цены</p>}
                        {configsList.length ? 
                            canUserGetPrice(configsList) && checkPricesReady(pricesArray, priceDataIsAbsence) ?
                                <CartHandler customRouter={CustomRouter}/>
                                :
                                <div className="product-price__cart product-price__cart-default">
                                    <div className='product-price__cart-title'>
                                        <p>Количество</p>
                                        <p>Цена</p>
                                    </div>
                                    <button className="button product-cart__button product-cart__button-empty-content" onClick={handleOpenPopUp}>
                                        Запросить цену
                                    </button>
                                </div>
                            : null}
                    </section>
                </>
        );
    } catch (error) {
        return <ErrorHandler error={error} />;
    }
};